import React, { useState } from 'react'

import Loader from '@space-lib/components/loader'
import { CommunityTypeProductConsume, ComponentNames } from '@space-lib/datahub'
import { DataHubEntities, DataHubService } from '@space-lib/datahub/hotmartClub'
import { useDeviceInfo } from '@space-lib/hooks/useDeviceInfo'
import { UserInfoRole } from '@space-shell/common/services/user'
import { useUserInfoStore } from '@space-shell/core/store/useUserInfoStore'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import useChangeCommunityTypeMutation from '@space-shell/modules/communities/hooks/useChangeCommunityTypeMutation/useChangeCommunityTypeMutation'

import { useGetCreateCommunityPermission } from '@space-shell/modules/communities/hooks/useGetCreateCommunityPermission'
import { CreateCommunityPermissionFeatureName } from '@space-shell/modules/communities/hooks/useGetCreateCommunityPermission/constans'

import ClickToChangeCommunityTypeSchema from '@space-shell/modules/home/datahub/schemas/click/community/clickToChangeCommunityTypeSchema'

import { ACCESS_ITEMS, COMMUNITY_TYPE } from '../../constants'
import { ContainerAccessSection } from '../../styles'
import ChangeCommunityTypeModal from '../changeCommunityTypeModal/changeCommunityTypeModal'
import { RadioCard } from '../radioCard'

const ChangeCommunityTypeContainer: React.FC = () => {
  const { hasPermission, isInitialLoading } = useGetCreateCommunityPermission({
    featureName: CreateCommunityPermissionFeatureName.COMMUNITY_PUBLIC
  })
  const { community } = useCommunityConfigurationSidebar()
  const [communityType, setCommunityType] = useState<COMMUNITY_TYPE>(community.type)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [pendingType, setPendingType] = useState<COMMUNITY_TYPE | null>(null)
  const deviceInfo = useDeviceInfo()
  const user = useUserInfoStore()
  const [removeUsersOption, setRemoveUsersOption] = useState<boolean>(false)

  const handleResetFields = () => {
    setOpenModal(false)
    setRemoveUsersOption(false)
    setPendingType(null)
  }

  const { onConfirmButton, isLoading } = useChangeCommunityTypeMutation({
    communityId: community.id,
    communityType: community.type,
    pendingType,
    setCommunityType,
    removeUsersOption,
    handleResetFields
  })

  const handleCommunityTypeChange = (type: COMMUNITY_TYPE) => {
    setOpenModal(true)
    setPendingType(type)

    DataHubService.sendEvent({
      entity: DataHubEntities.Community,
      schema: new ClickToChangeCommunityTypeSchema({
        communityId: community.id,
        communityType: CommunityTypeProductConsume[type],
        deviceInfo,
        userRole: user.role || UserInfoRole.VIEWER,
        componentName: ComponentNames.ACCESS_AND_PRIVACY_TAB
      })
    })
  }

  const isPaidCommunity = community.type === COMMUNITY_TYPE.PAID

  if (isInitialLoading) {
    return (
      <div className='_mt-3'>
        <Loader full />
      </div>
    )
  }

  return (
    <>
      <ContainerAccessSection className='_d-flex _flex-column _mb-7'>
        {hasPermission && (
          <RadioCard
            onChange={handleCommunityTypeChange}
            ariaLabelledBy='public'
            value={COMMUNITY_TYPE.PUBLIC}
            checked={communityType === COMMUNITY_TYPE.PUBLIC}
            title={ACCESS_ITEMS.public.title}
            description={ACCESS_ITEMS.public.description}
            isDisabled={isPaidCommunity || community.isLocked}
          />
        )}
        <RadioCard
          onChange={handleCommunityTypeChange}
          ariaLabelledBy='open'
          value={COMMUNITY_TYPE.OPEN}
          checked={communityType === COMMUNITY_TYPE.OPEN}
          title={ACCESS_ITEMS.open.title}
          description={ACCESS_ITEMS.open.description}
          isDisabled={isPaidCommunity || community.isLocked}
        />
        <RadioCard
          onChange={handleCommunityTypeChange}
          ariaLabelledBy='exclusive'
          value={COMMUNITY_TYPE.EXCLUSIVE}
          checked={communityType === COMMUNITY_TYPE.EXCLUSIVE}
          title={ACCESS_ITEMS.exclusive.title}
          description={ACCESS_ITEMS.exclusive.description}
          isDisabled={isPaidCommunity || community.isLocked}
        />
        {community.type === COMMUNITY_TYPE.PAID && (
          <RadioCard
            onChange={handleCommunityTypeChange}
            ariaLabelledBy='paid'
            value={COMMUNITY_TYPE.PAID}
            checked={communityType === COMMUNITY_TYPE.PAID}
            title={ACCESS_ITEMS.paid.title}
            description={ACCESS_ITEMS.paid.description}
            isDisabled
          />
        )}
      </ContainerAccessSection>
      <ChangeCommunityTypeModal
        onConfirmButton={onConfirmButton}
        isOpen={openModal}
        onClose={handleResetFields}
        communityType={community.type}
        pendingType={pendingType as COMMUNITY_TYPE}
        removeUsersOption={removeUsersOption}
        onRemoveUsersOptionChange={(value: boolean) => setRemoveUsersOption(value)}
        isLoading={isLoading}
      />
    </>
  )
}

export default ChangeCommunityTypeContainer
